/**
 * @file customer-copy.ts
 * @module utils/customer-copy
 * @description Thermal printer integration for the uTrade customer copy receipt workflow.
 *   Exports `ThermalPrinterService`, which queues print jobs via backend relay agent.
 *   Also exports `CustomerCopyOrder` and related types.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.2.0
 * @version 1.2.0
 */
import APICallService from "../api/apiCallService";
import { routePlanning } from "../api/apiEndPoints";

/**
 * Represents a customer address as stored on a uTrade order.
 * `fullAddress` is the pre-formatted single string used on the receipt.
 */
type CustomerCopyAddress = {
  name: string;
  addressLine1: string;
  landmark: string;
  fullAddress: string;
};

/**
 * A single line-item on the customer copy receipt.
 * Corresponds to one product variant loaded onto the delivery vehicle.
 */
type CustomerCopyVariant = {
  productName: string;
  quantity: string;
  quantityType: number;
  stockCount: number;
  paidStockCount?: number;
  freeStockCount?: number;
  unitPrice: number;
  unitPriceFormatted: string;
  totalAmount: number;
  totalAmountFormatted: string;
  /** Present on promotional free rows and printed as the applied offer. */
  offerLabel?: string;
  promotionLine?: boolean;
};

/**
 * Complete data payload for a single customer copy receipt.
 *
 * Returned by the backend endpoint `routePlanning.orderInvoiceForThermalPrinter`
 * (GET `/_product/manage/route-planning/order-invoice-thermal-printer`) and
 * consumed by `ThermalPrinterService.printCustomerCopy()`.
 *
 * One `CustomerCopyOrder` maps to one physical receipt printed and cut by
 * the thermal printer.
 */
export type CustomerCopyOrder = {
  /** Seller / warehouse company name, printed in the receipt header. */
  companyName: string;
  /** Order type label, e.g. "Regular Order". */
  type: string;
  orderId: string;
  orderDate: string;
  /** Human-readable order date string, e.g. "01 Jan 2026". */
  orderDateFormatted: string;
  customerName: string;
  billingAddress: CustomerCopyAddress;
  shippingAddress: CustomerCopyAddress;
  /** Line items for all variants loaded on this order. */
  variants: CustomerCopyVariant[];
  summary: {
    totalAmount: number;
    totalAmountFormatted: string;
  };
  /** Bank account details for payment, printed at the bottom of the receipt. */
  bankDetails: {
    /** Warehouse name associated with this order. */
    warehouse: string;
    districtName: string;
    CRDB: number;
    NMB: number;
    STANBIC: number;
    NBC: string;
  };
  vehicle: {
    name: string;
    /** Vehicle registration / plate number. */
    number: string;
  };
  driver: {
    name: string;
    phoneCountry: string;
    phone: string;
  };
  /** Optional free-text note printed at the bottom of the receipt. */
  note?: string;
};

/**
 * Queue a thermal printer job via backend relay agent.
 *
 * The backend queues the print job to the database. A relay agent running
 * at each warehouse polls for pending jobs and POSTs directly to the local
 * thermal printer. This avoids any TLS connection from the browser to the
 * printer, so the admin app's tab is never flagged "Not Secure".
 *
 * ## Usage
 * ```ts
 * const svc = new ThermalPrinterService('192.168.0.100');
 * await svc.printCustomerCopy(order);
 * ```
 *
 * @see CustomerCopyOrder for the receipt data shape
 * @see utrade-backend ReportsController.printCustomerCopyOnThermalPrinter
 * @see print-relay-agent PrintJobPoller
 */
export class ThermalPrinterService {
  private printerIp: string;
  private warehouseId: string | null;

  /**
   * @param printerIp IPv4 address of the thermal printer on the local warehouse LAN,
   *                  e.g. `"192.168.0.100"`. Optionally includes port: `"192.168.0.100:8000"`.
   *                  Saved to and restored from `localStorage` by the `CustomerCopyModal`.
   * @param warehouseId ID of the currently selected warehouse, sent as the `warehouse`
   *                  header so the backend queues the job for the correct relay agent.
   */
  constructor(printerIp: string, warehouseId?: string | null) {
    this.printerIp = printerIp;
    this.warehouseId = warehouseId || null;
  }

  /**
   * Queue a customer copy receipt for printing via warehouse relay agent.
   *
   * Returns immediately with jobId (non-blocking). The relay agent will
   * process the job asynchronously at the warehouse.
   *
   * @param order Receipt data for one order, as returned by the backend.
   * @returns Resolves `true` when the job is successfully queued.
   * @throws If validation fails or backend is unreachable.
   */
  async printCustomerCopy(order: CustomerCopyOrder): Promise<boolean> {
    return this.printCustomerCopyRelay(order);
  }

  /**
   * Queue the job via backend relay agent (async).
   * Backend returns immediately with jobId; relay agent processes asynchronously.
   *
   * @see utrade-backend ReportsController.printCustomerCopyOnThermalPrinter
   * @see print-relay-agent PrintJobPoller
   */
  private async printCustomerCopyRelay(
    order: CustomerCopyOrder,
  ): Promise<boolean> {
    const apiService = new APICallService(
      routePlanning.printCustomerCopy,
      { printerIp: this.printerIp, order },
      '',
      '',
      '',
      '',
      undefined,
      this.warehouseId,
    );
    const response: { success: boolean; jobId?: string } = await apiService.callAPI();
    if (!response?.success) {
      throw new Error("Failed to queue print job.");
    }
    // jobId is returned by async relay agent mode
    if (response.jobId) {
      console.log(`[ThermalPrinter] Job queued: ${response.jobId}`);
    }
    return true;
  }
}
